import { marked } from 'marked';
import DOMPurify from 'dompurify';
import katex from 'katex';
import 'katex/dist/katex.min.css';

marked.setOptions({
	gfm: true,
	breaks: true
});

/**
 * Turn TeX into KaTeX HTML. Incomplete stream fragments stay as text.
 * @param {string} tex
 * @param {boolean} displayMode
 * @returns {string}
 */
function renderTex(tex, displayMode) {
	try {
		return katex.renderToString(tex, {
			throwOnError: false,
			strict: false,
			displayMode: !!displayMode,
			output: 'html'
		});
	} catch (error) {
		return tex;
	}
}

/**
 * Replace UTF-8 replacement chars that stand in for an en-dash in model output.
 * @param {string} source
 * @returns {string}
 */
function repairBrokenUnicode(source) {
	return String(source)
		.replace(/\\text\{\uFFFD\}/g, '–')
		.replace(/(\d)\uFFFD(\d)/g, '$1–$2');
}

marked.use({
	extensions: [
		{
			name: 'blockKatex',
			level: 'block',
			start: function(src) {
				const i = src.indexOf('$$');
				return i < 0 ? undefined : i;
			},
			tokenizer: function(src) {
				const match = src.match(/^\$\$([\s\S]+?)\$\$/);
				if (!match) return undefined;
				return {
					type: 'blockKatex',
					raw: match[0],
					text: match[1].trim(),
					displayMode: true
				};
			},
			renderer: function(token) {
				return renderTex(token.text, true);
			}
		},
		{
			name: 'inlineKatex',
			level: 'inline',
			start: function(src) {
				const i = src.indexOf('$');
				return i < 0 ? undefined : i;
			},
			tokenizer: function(src) {
				const display = src.match(/^\$\$([^$\n]+)\$\$/);
				if (display) {
					return {
						type: 'inlineKatex',
						raw: display[0],
						text: display[1],
						displayMode: true
					};
				}
				const inline = src.match(/^\$([^$\n]+?)\$/);
				if (inline) {
					return {
						type: 'inlineKatex',
						raw: inline[0],
						text: inline[1],
						displayMode: false
					};
				}
				return undefined;
			},
			renderer: function(token) {
				return renderTex(token.text, token.displayMode);
			}
		}
	]
});

/**
 * Convert markdown to sanitized HTML for assistant chat bubbles.
 * @param {string} markdown
 * @returns {string}
 */
export function renderMarkdown(markdown) {
	const source = markdown == null ? '' : String(markdown);
	if (!source) return '';
	const html = marked.parse(repairBrokenUnicode(source), { async: false });
	return DOMPurify.sanitize(html, {
		USE_PROFILES: { html: true },
		ADD_ATTR: ['aria-hidden', 'style']
	});
}
